import { Map, List } from "immutable";

import { APP_ACTIONS } from "app/app.constants";
import { ACTIONS } from "features/planner/planner.constants";
import { CardMapper, EmployeeMapper } from "features/planner/planner.mappers";
import { ACTIONS as EMPLOYEE_ACTIONS } from "features/planner/employee/employee.constants";

const initialState = Map();

export default (state = initialState, action) => {
  const { error, payload, type } = action;

  switch (type) {
    case ACTIONS.CREATE_VIEW_GROUPS:
    case ACTIONS.GET_VIEW: {
      if (error) return state;

      // get all cards in view
      const cards = payload.viewGroups.reduce((cards, viewGroup) => {
        const cardContainers = viewGroup.group.cardContainers;

        return cards.concat(
          cardContainers.reduce(
            (cardContainerCards, cardContainer) =>
              cardContainerCards.concat(cardContainer.cards),
            [],
          ),
        );
      }, []);
      return Map(
        cards.map(card => [card.cardId.toString(), CardMapper.from(card)]),
      );
    }

    case ACTIONS.GET_VIEW_GROUP: {
      if (error) return state;

      const cards = payload.group.cardContainers
        .reduce((result, container) => [...result, ...container.cards], [])
        .map(card => [card.cardId.toString(), CardMapper.from(card)]);

      return state.merge(cards);
    }

    case ACTIONS.GET_CARDS:
    case ACTIONS.GET_SUB_CONTRACTORS:
    case ACTIONS.GET_GROUP_CARDS: {
      if (error) return state;

      return Map(
        List(payload).map(Card => [
          Card.cardId.toString(),
          CardMapper.from(Card),
        ]),
      );
    }

    case ACTIONS.SEARCH_VIEW: {
      if (error) return state;

      const { cards } = payload;

      return state.merge(
        Map(
          List(cards).map(Card => [
            Card.cardId.toString(),
            CardMapper.from(Card),
          ]),
        ),
      );
    }

    case ACTIONS.CREATE_SUB_CONTRACTOR:
    case ACTIONS.UPDATE_SUB_CONTRACTOR:
    case ACTIONS.GET_SUB_CONTRACTOR:
    case ACTIONS.GET_CARD:
    case ACTIONS.CREATE_CARD:
    case ACTIONS.EDIT_CARD: {
      if (error) return state;

      return state.set(payload.cardId.toString(), CardMapper.from(payload));
    }

    case ACTIONS.DELETE_SUB_CONTRACTOR: {
      if (action.error) return state;

      const { cardId } = action;

      return state.delete(cardId.toString());
    }

    case ACTIONS.DELETE_CARD: {
      if (action.error) return state;

      const { cardId } = action;

      return state.delete(cardId.toString());
    }

    case ACTIONS.CREATE_CARD_MOVE: {
      const {
        cardId,
        sourceSlotNo,
        sourceGroupId,
        conflictingCardId,
        sourceCardContainerId,
      } = action;

      const { employee } = action.payload;

      if (!error) {
        if (!!conflictingCardId) {
          state = state.delete(conflictingCardId.toString());
        }
        if (!!employee) {
          state = state.update(cardId.toString(), card =>
            card.set("employee", EmployeeMapper.from(employee)),
          );
        }

        return state;
      }

      // If server fails during card move - reset card position
      if (error && !!cardId) {
        return state.update(cardId.toString(), card =>
          card
            .set("cardContainerId", sourceCardContainerId)
            .set("groupId", sourceGroupId)
            .set("slotNo", sourceSlotNo),
        );
      }

      return state;
    }

    case EMPLOYEE_ACTIONS.UPDATE_EMPLOYEE: {
      if (action.error) return state;

      return state.mergeIn(
        [action.cardId.toString(), "employee"],
        EmployeeMapper.from(payload),
      );
    }

    case EMPLOYEE_ACTIONS.HIDE_EMPLOYEE_IN_PLANNER: {
      if (action.error) return state;

      return state.update(action.id.toString(), card =>
        card.setIn(["employee", "hideInPlanning"], true),
      );
    }

    case EMPLOYEE_ACTIONS.SHOW_EMPLOYEE_IN_PLANNER: {
      if (action.error) return state;

      return state.update(action.cardId.toString(), card =>
        card.setIn(["employee", "hideInPlanning"], false),
      );
    }

    case ACTIONS.CLEAR_CARDS: {
      return initialState;
    }

    case APP_ACTIONS.ASYNC_START: {
      switch (action.subtype) {
        case ACTIONS.CREATE_CARD_MOVE: {
          if (error) return state;

          const {
            cardId,
            targetSlotNo,
            targetGroupId,
            targetCardContainerId,
          } = action;

          return state.update(cardId.toString(), card =>
            card
              .set("cardContainerId", targetCardContainerId)
              .set("groupId", targetGroupId)
              .set("slotNo", targetSlotNo),
          );
        }

        default:
          return state;
      }
    }

    case ACTIONS.ADD_EMPLOYEES_TO_GROUP: {
      return state.merge(
        payload.map(Card => [Card.cardId.toString(), CardMapper.from(Card)]),
      );
    }

    default:
      return state;
  }
};
